// @ts-strict-ignore
import {
  Box, ButtonSize, ButtonVariant, IconName,
} from '@usealloy/component-library';
import { getProperty } from 'dot-prop';
import isEqual from 'lodash/isEqual';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { booleanRuleStrings } from 'flow/store/engine/constants';
import type {
  BooleanRule, IndexedRule, PopulatedThreshold, RuleObject,
} from 'flow/store/engine/types';
import type { MLAAttributeItemOrigin } from 'flow/editor/components/NodeEditorDrawer/types';
import { CaretPositionSuffixTypes, RuleEditorDropdownMode } from 'flow/editor/components/NodeEditorDrawer/types';
import { ContainerType } from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/types';
import { createStringOfPathArray } from 'flow/store/engine/utils';
import { getNumericPathIndices } from 'flow/service/actions';
import { getOperatorUsedInRuleObject } from 'flow/store/engine/update/helpers';
import { getRulePathInDotNotationFormat, isMLAAttributeItemOrigin, isThresholdRightAttributeOrigin } from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/helpers';
import MLAAttributeContainer from 'flow/editor/components/mla/MLAAttributeContainer/MLAAttributeContainer';
import { reorderActionRuleItem, setSelectedEvidenceThresholdId } from 'flow/store/redux/slice';
import { RuleEditorContext } from 'flow/editor/components/NodeEditorDrawer/context';
import RuleLine from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/RuleLine';
import {
  selectIsEditingLinkedChildAction,
  selectLegacyMappings,
  selectPublicVersion,
  selectWorkflowData,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';

import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { UserContext } from '~/dashboard/context';
import { reversedObjectKeyVal } from '~/entities/evaluations/TagRuleModal/utils';

import {
  checkIfRuleHasAndKey,
  checkIfRuleHasOrKey,
  getLineIndices,
  getParentRulePathIndicies,
  getRuleFromLine,
  getThresholdFromActionUsingRulePath,
} from './helpers';
import { IsolatedTextEditingContainer } from '../IsolatedTextEditingContainer';
import MorphingCaret from '../MorphingCaret';
import RuleLineItemOutputValue from './RuleLineItemOutputValue';
import RuleLineThresholdItemWrapper from './RuleLineThresholdItemWrapper';
import RuleThresholdDropdown from '../RuleThresholdDropdown';
import RuleClause from './RuleClause';
import {
  StyledEvidencesButton,
  StyledHighlighter,
  StyledIndentation,
  StyledLineBreakDivider,
  StyledOutputValueWrapper,
  StyledRuleAndOutputWrapper,
  StyledRuleItemRow,
  StyledRuleLineItem,
  StyledRuleLineItemWrapper,
} from './styles';
import {
  ambiguousRuleOperatorType,
  validGroupingCaretTypes,
  validLeftSideCaretTypes,
  validRightSideAttributeCaretTypes,
} from './constants';
import { ThresholdContext } from '../context';
import { getThresholdLeftDataType, getThresholdType, populateThresholdData } from 'flow/service/thresholds';
import { selectSelectedEvidenceThresholdId, selectThresholdOutputResults } from 'flow/store/redux/selectors/workflowFeatures';
import { datadogRum } from '@datadog/browser-rum';
import { useEditorAccess } from 'flow/editor/context';
import { getIsEditable } from 'flow/service/general';
import { setThresholdSimParamsByPublicId } from '~/flow/thresholdSimulator/redux/slice';
import { formatThresholdSimParams } from '../../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/ThresholdSimulatorModal/helpers';
import useThresholdSimulatorParams from 'flow/api/useThresholdSimulatorParams';

interface RuleLineItemProps {
  editable?: boolean;
  lineIndex: number;
}

function RuleLineItem({
  editable = true,
  lineIndex,
}: RuleLineItemProps) {
  const dispatch = useAppDispatch();
  const { actions, thresholds } = useAppSelector(selectWorkflowData);
  const { reasonCodeGroups, services } = useAppSelector(selectWorkflowProps);
  const isEditingLinkedChildAction = useAppSelector(selectIsEditingLinkedChildAction);
  const legacyMappings = useAppSelector(selectLegacyMappings);
  const thresholdOutputResults = useAppSelector(selectThresholdOutputResults);
  const currentThresholdId = useAppSelector(selectSelectedEvidenceThresholdId);
  const publicVersion = useAppSelector(selectPublicVersion);

  const [isRuleItemHighlighted, setRuleItemHighlighted] = useState(false);
  const { featureFlags } = useContext(UserContext);

  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);

  const {
    semanticLineBreakData,
    sourceAction,
    pendingGroupOpeningParenthesisInfo,
    pendingGroupMaxRange: {
      itemsWithinMaxRange,
    },
    simpleEditorMode,
    isDragging,
    isDragEnabled,
    isDragItemHoveringOnDropZone,
    setIsDragItemHoveringOnDropZone,
    renderRuleNumbers,
    isolatedEditingBlock,
    thresholdResults,
    containerType,
    selectedEvidenceThresholdId,
    containsAmbiguousRuleOperator,
    setContainsAmbiguousRuleOperator,
    setIsDragging,
  } = useContext(RuleEditorContext);
  const [, drag, dragPreview] = useDrag(() => ({
    type: 'line',
    item: () => { // fired at the beginning of the drag operation
      setIsDragging(true);
      return { lineIndex };
    },
    end: () => {
      setIsDragging(false);
      setIsDragItemHoveringOnDropZone(undefined);
    },
  }));

  const onReorderLine = ({
    draggedLineIndex,
    dropTargetLineIndex,
  }: {
    draggedLineIndex: number;
    dropTargetLineIndex: number;
  }) => {
    const draggedLineIndices = getLineIndices({
      semanticLineBreakData,
      lineNumberIndex: draggedLineIndex,
    });
    const dropTargetLineIndices = getLineIndices({
      semanticLineBreakData,
      lineNumberIndex: dropTargetLineIndex,
    });

    const draggedLineRule = getRuleFromLine({
      sourceAction,
      lineIndices: draggedLineIndices,
    });
    const dropTargetLineRule = getRuleFromLine({
      sourceAction,
      lineIndices: dropTargetLineIndices,
    });

    dispatch(reorderActionRuleItem({
      id: sourceAction.publicId,
      draggedLineRule,
      dropTargetLineRule,
    }));
  };

  const [, drop] = useDrop(() => ({
    accept: 'line',
    canDrop: () => isDragEnabled,
    hover: () => { if (isDragEnabled) setIsDragItemHoveringOnDropZone(lineIndex); },
    drop: item => {
      onReorderLine({
        draggedLineIndex: (item as { lineIndex: number }).lineIndex,
        dropTargetLineIndex: lineIndex,
      });
    },
  }), [isDragEnabled, semanticLineBreakData, dispatch]);

  const lineItems = semanticLineBreakData?.groupedByLines[lineIndex]?.items;

  const lineEndsWithFinalRightAttributeParts = useMemo(() => (
    validRightSideAttributeCaretTypes.some(char => lineItems.find(item => item.endsWith(char)))
  ), [lineItems]);

  const lineEndsWithParenthesis = useMemo(() => (
    validGroupingCaretTypes.some(char => lineItems.find(item => item.endsWith(char)))
  ), [lineItems]);

  const lineContainsThresholdCarets = useMemo(() => (
    lineEndsWithFinalRightAttributeParts
    || validLeftSideCaretTypes.some(char => lineItems.find(item => item.endsWith(char)))
    || !!lineItems.find(item => item.endsWith(CaretPositionSuffixTypes.THRESHOLD_OPERATOR))
  ), [lineEndsWithFinalRightAttributeParts, lineItems]);

  const isEditingLinkedChildAndRuleThenExists = useMemo(() => (
    isEditingLinkedChildAction
    // When lineItems includes RULE_THEN_ACTION_TYPE & RULE_THEN_ACTION_VALUE,
    // We need to grab the last rule before those two items:
    && lineItems.find(item => item.endsWith(CaretPositionSuffixTypes.RULE_THEN_ACTION_TYPE))
    && lineItems.length > 1
  ), [isEditingLinkedChildAction, lineItems]);

  const lineItemLastIndexToGrab = isEditingLinkedChildAndRuleThenExists ? -3 : -1;
  const lineItemFlatCaret = lineItems.at(lineItemLastIndexToGrab);

  const rulePath = getRulePathInDotNotationFormat({
    itemFlatCaret: lineItemFlatCaret,
    isThresholdCaretPosition: true,
    sourceAction,
  });
  const rulePathNumericIndices = getNumericPathIndices(rulePath, 'rules');

  const rules = ('id' in (sourceAction.rules as RuleObject))
    ? sourceAction.rules as IndexedRule
    : getProperty(sourceAction.rules, rulePath) as IndexedRule;

  const threshold = rules ? thresholds[rules.id] : null;

  const thresholdSimQueryResponse = useThresholdSimulatorParams({
    publicId: threshold ? threshold.publicId : null,
    workflowVersion: publicVersion,
    enabled: featureFlags.thresholdSimulator,
  });

  useEffect(() => {
    if (threshold && featureFlags.thresholdSimulator) {
      dispatch(setThresholdSimParamsByPublicId(
        formatThresholdSimParams(threshold.publicId, thresholdSimQueryResponse),
      ));
    }
  }, [threshold, dispatch, featureFlags, thresholdSimQueryResponse]);

  const populatedThreshold = React.useMemo<PopulatedThreshold>(() => (
    threshold
      ? populateThresholdData({
        actions, reasonCodeGroups, services, threshold,
      })
      : null
  ), [actions, reasonCodeGroups, services, threshold]);

  const thresholdContext = React.useMemo(() => (
    populatedThreshold
      ? {
        threshold: populatedThreshold,
        thresholdType: getThresholdType(populatedThreshold),
        leftDataType: getThresholdLeftDataType(populatedThreshold),
        rules,
      }
      : {
        threshold: undefined,
        thresholdType: undefined,
        leftDataType: undefined,
        rules,
      }
  ), [populatedThreshold, rules]);

  const isPartOfPossibleThresholdsScope = useMemo(() => {
    if (!lineItems.length) return false;

    if (lineItemFlatCaret.endsWith(CaretPositionSuffixTypes.GROUP_START)) {
      return false;
    }

    const rulePathWithCaretPosition = getRulePathInDotNotationFormat({
      itemFlatCaret: lineItems.at(lineItemLastIndexToGrab),
      isThresholdCaretPosition: lineContainsThresholdCarets,
      sourceAction,
    });

    const rule = getProperty(sourceAction.rules, rulePathWithCaretPosition) as RuleObject;
    if (!rule) return false;

    return Array.isArray(itemsWithinMaxRange)
      ? (itemsWithinMaxRange as (IndexedRule | BooleanRule)[])?.find(
        item => {
          // if rule is a threshold, we check thershold id is matching
          if ('id' in rule && rule.id === (item as IndexedRule).id) return true;
          // if rule is not a threshold but nested and/or array, we check the equality of the nested ruleset
          if ((checkIfRuleHasAndKey(rule) || checkIfRuleHasOrKey(rule))
            && isEqual(rule, item)) return true;
          return false;
        })
      : false;
  }, [
    itemsWithinMaxRange,
    lineItemFlatCaret,
    lineItems,
    lineContainsThresholdCarets,
    lineItemLastIndexToGrab,
    sourceAction,
  ]);

  const PendingGroupEndParenthesisCaret = useMemo(() => {
    if (
      pendingGroupOpeningParenthesisInfo.path
      && isPartOfPossibleThresholdsScope
    ) {
      // flattened path meaning we convert expression path (e.g. [0][1]..) into 0-1 - which is the format we use for caret position / caret indices
      // if the rule is included in the possible scope, we display the caret dropdown. the reason why this lives in RuleGroup is because we have to display
      // this "possible ending parenthesis" caret dropdown all the way at the end of a rule group (and/or array or a threshold)
      // and determining what's the last dropdown shown for those in RuleThresholdDropdown or Threshold is either impossible (not covering nested and/or s)
      // or overly complex. by doing this here, we achieve without having to calculate what was the last dropdown.
      const pathIndices = (
        !lineItems.at(-1).endsWith(CaretPositionSuffixTypes.GROUP_END)
        && !lineItems.at(0).endsWith(CaretPositionSuffixTypes.GROUP_END)
      ) ? getParentRulePathIndicies(getNumericPathIndices(lineItemFlatCaret, 'rules'))
        : getNumericPathIndices(lineItemFlatCaret, 'rules');

      const flattenedPathInCaretPositionFormat = pathIndices.join('-');
      const parentRulePath = createStringOfPathArray(pathIndices);
      return (
        <MorphingCaret
          position={flattenedPathInCaretPositionFormat}
          parentRulePath={parentRulePath}
          mode={CaretPositionSuffixTypes.GROUP_END}
          inlineSuggestion
        >
          {({ caretDropdownMode }) => (
            <RuleThresholdDropdown
              parentRulePath={parentRulePath}
              mode={caretDropdownMode}
              insertion
            />
          )}
        </MorphingCaret>
      );
    }

    return null;
  }, [
    isPartOfPossibleThresholdsScope,
    lineItems,
    lineItemFlatCaret,
    pendingGroupOpeningParenthesisInfo.path,
  ]);

  const parentRule = useMemo(() => {
    const parentPathIndices = rulePathNumericIndices.slice(
      0,
      ['and', 'or'].includes(rulePathNumericIndices.at(-1))
        ? rulePathNumericIndices.length - 1
        : rulePathNumericIndices.length - 2,
    );
    const parentRulePath = createStringOfPathArray(parentPathIndices);
    return getProperty(sourceAction.rules, parentRulePath) as BooleanRule;
  }, [rulePathNumericIndices, sourceAction.rules]);

  const ambiguousBooleanRuleOperatorType = useMemo(() => {
    // ambiguous boolean rule is only supported for viewing it. these .ambiguous key may get added while workflow data is transformed when loading the editor,
    // if the wf was configured in the legacy editor like this: th1 and th2 or th3
    if (parentRule?.ambiguous) {
      const parentRuleOperator = getOperatorUsedInRuleObject({ rule: parentRule });
      if (
        (rulePathNumericIndices.at(-1) !== '0' || (parentRuleOperator && parentRule[parentRuleOperator]?.length === 1))
        && rulePathNumericIndices.length > 2
      ) {
        // checks if this is not very top level in the rule
        // or if last path index indicates 0 meaning first item in the scope (group),
        // then checks if the rule contains only one item in it (in that case we'll allow this)
        return ambiguousRuleOperatorType.WITHIN_AMBIGUOUS_RULE;
      }
      return ambiguousRuleOperatorType.BEFORE_AMBIGUOUS_RULE;
    }
    return false;
  }, [parentRule, rulePathNumericIndices]);

  const isInAmbiguousBooleanRuleScope = useMemo(() => {
    if (ambiguousBooleanRuleOperatorType) return ambiguousBooleanRuleOperatorType;

    let topLevelOperator;
    const rule = parentRule ?? sourceAction.rules as BooleanRule;
    if (rule) {
      if ('and' in rule) {
        topLevelOperator = 'and';
      } else if ('or' in rule) {
        topLevelOperator = 'or';
      }

      if (rule?.[topLevelOperator]) {
        const findAmbiguousRule = rule[topLevelOperator].filter(item => item.ambiguous);
        return findAmbiguousRule.length > 0;
      }
    }
    return false;
  }, [ambiguousBooleanRuleOperatorType, parentRule, sourceAction.rules]);

  const lineItemDepth = useMemo(() => {
    const operatorsInPath = lineItems.at(-1)?.match(/\band\b|\bor\b/g) as string[];
    const isFirstItemNotRuleOperatorOrLeftAction = !lineEndsWithParenthesis
      && ![
        CaretPositionSuffixTypes.RULE_OPERATOR,
        CaretPositionSuffixTypes.THRESHOLD_LEFT_ACTION,
      ].some(suffix => lineItems[0]?.includes(suffix));

    let depth = 0;
    if (isFirstItemNotRuleOperatorOrLeftAction) depth = 1;
    if (operatorsInPath?.length) depth = operatorsInPath.length - 1;
    if (ambiguousBooleanRuleOperatorType) return depth - 1;
    if ('id' in (sourceAction.rules as RuleObject)) return depth - 1;
    return depth;
  }, [ambiguousBooleanRuleOperatorType, lineEndsWithParenthesis, lineItems, sourceAction]);

  useEffect(() => {
    if (ambiguousBooleanRuleOperatorType) {
      setContainsAmbiguousRuleOperator(true);
    }
  }, [ambiguousBooleanRuleOperatorType, setContainsAmbiguousRuleOperator]);

  const thresholdInThisLine = useMemo(() => (
    lineContainsThresholdCarets
      ? getThresholdFromActionUsingRulePath({
        sourceAction,
        actions,
        thresholds,
        reasonCodeGroups,
        services,
        rulePath,
      })
      : null
  ), [
    actions,
    lineContainsThresholdCarets,
    reasonCodeGroups,
    rulePath,
    services,
    sourceAction,
    thresholds,
  ]);

  const mlaEditorEnabledForThresholdAttribute = useMemo(() => {
    const lineItemIncludesLeftOrRightAttribute = lineItems.find(item => (
      item.endsWith(CaretPositionSuffixTypes.THRESHOLD_LEFT_ATTRIBUTE)
      || item.endsWith(CaretPositionSuffixTypes.THRESHOLD_RIGHT_ATTRIBUTE)
    ));
    if (
      isolatedEditingBlock.originPublicId
      && isolatedEditingBlock.originPublicId === thresholdInThisLine?.publicId
      && lineItemIncludesLeftOrRightAttribute
      && isMLAAttributeItemOrigin(isolatedEditingBlock.originType)
    ) {
      return isolatedEditingBlock.originType;
    }

    return false;
  }, [isolatedEditingBlock, lineItems, thresholdInThisLine]);

  const isolatedTextEditingAreaEnabled = useMemo(() => {
    if (isolatedEditingBlock.originPublicId
      && isolatedEditingBlock.originPublicId === thresholdInThisLine?.publicId
      && lineItems.find(caret => caret === isolatedEditingBlock.originCaretIndex)
      && isThresholdRightAttributeOrigin(isolatedEditingBlock.originType)
    ) {
      return isolatedEditingBlock.originPublicId;
    }

    return false;
  }, [isolatedEditingBlock, thresholdInThisLine, lineItems]);

  const isolatedEditingAreaEnabledForAnotherThreshold = isolatedEditingBlock.originPublicId
    && isolatedEditingBlock.originPublicId !== thresholdInThisLine?.publicId;

  const reversedActionMappings = useMemo(() => (
    reversedObjectKeyVal(legacyMappings?.actions || {})
  ), [legacyMappings]);

  const legacyActionId = useMemo(() => (
    reversedActionMappings?.[sourceAction?.publicId] || null
  ), [sourceAction?.publicId, reversedActionMappings]);

  const thresholdResult = useMemo(() => {
    const thresholdResultOfThisAction = featureFlags.ruleExplainabilityPhase >= 4
      ? thresholdOutputResults?.[legacyActionId]
      : thresholdResults;
    const reversedThresholdMappings = reversedObjectKeyVal(
      legacyMappings?.thresholds || {},
    );

    if (
      thresholdResultOfThisAction
      && thresholdInThisLine
      && Object.keys(reversedThresholdMappings).length
    ) {
      const legacyThresholdId = reversedThresholdMappings?.[thresholdInThisLine?.publicId];
      return thresholdResultOfThisAction.thresholds?.[legacyThresholdId] || null;
    }
    return null;
  }, [featureFlags, legacyActionId, legacyMappings,
    thresholdInThisLine, thresholdOutputResults, thresholdResults]);

  const getRuleOperatorMode = useCallback((defaultOperator: RuleEditorDropdownMode) => {
    if ((ambiguousBooleanRuleOperatorType === ambiguousRuleOperatorType.WITHIN_AMBIGUOUS_RULE)
      && lineItemFlatCaret) {
      if (!lineItemFlatCaret.endsWith(booleanRuleStrings.AND_OPERATOR_STRING)
        && !lineItemFlatCaret.endsWith(booleanRuleStrings.OR_OPERATOR_STRING)) {
        return defaultOperator;
      }
      const boolOperatorList = (lineItemFlatCaret.match(/\band\b|\bor\b/g) as string[]);
      return boolOperatorList.at(-1) === booleanRuleStrings.OR_OPERATOR_STRING
        ? RuleEditorDropdownMode.RULE_OPERATOR_OR
        : RuleEditorDropdownMode.RULE_OPERATOR_AND;
    }

    return defaultOperator;
  }, [ambiguousBooleanRuleOperatorType, lineItemFlatCaret]);

  // eslint-disable-next-line consistent-return
  const renderRuleThresholdDropdown = useCallback((item: string) => {
    const ruleThresholdDropDownMap = {
      [CaretPositionSuffixTypes.GROUP_START_PENDING]: {
        mode: RuleEditorDropdownMode.GROUP_START_PENDING,
        isRuleOperator: false,
      },
      [CaretPositionSuffixTypes.GROUP_START]: {
        mode: RuleEditorDropdownMode.GROUP_START,
        isRuleOperator: false,
      },
      [CaretPositionSuffixTypes.GROUP_END]: {
        mode: RuleEditorDropdownMode.GROUP_END,
        isRuleOperator: false,
      },
      [CaretPositionSuffixTypes.RULE_OPERATOR_AND]: {
        mode: RuleEditorDropdownMode.RULE_OPERATOR_AND,
        isRuleOperator: true,
      },
      [CaretPositionSuffixTypes.RULE_OPERATOR_OR]: {
        mode: RuleEditorDropdownMode.RULE_OPERATOR_OR,
        isRuleOperator: true,
      },
    };

    const keys = Object.keys(ruleThresholdDropDownMap);
    for (const key of keys) {
      if (item.endsWith(key)) {
        const { mode, isRuleOperator } = ruleThresholdDropDownMap[key];
        return (
          <RuleThresholdDropdown
            andOrPath={(
              isRuleOperator
                ? getRulePathInDotNotationFormat({
                  itemFlatCaret: item,
                  isRuleOperator,
                  sourceAction,
                })
                : undefined
            )}
            editable={editable}
            inAmbiguousScope={isRuleOperator ? Boolean(isInAmbiguousBooleanRuleScope) : false}
            key={item}
            mode={isRuleOperator ? getRuleOperatorMode(mode) : mode}
            parentRulePath={getRulePathInDotNotationFormat({
              itemFlatCaret: item,
              sourceAction,
            })}
          />
        );
      }
    }
  }, [editable, getRuleOperatorMode, isInAmbiguousBooleanRuleScope, sourceAction]);

  const canDrop = isDragEnabled
    && isDragItemHoveringOnDropZone === lineIndex
    && (lineEndsWithFinalRightAttributeParts || lineEndsWithParenthesis);

  const isLineDraggable = isDragEnabled
    && !lineEndsWithParenthesis
    && !containsAmbiguousRuleOperator
    && !pendingGroupOpeningParenthesisInfo.path
    && inEditMode;

  const evidenceButtonHandler = useCallback(() => {
    const thresholdId = thresholdResult?.action_threshold_id;
    if (currentThresholdId === thresholdId) {
      dispatch(setSelectedEvidenceThresholdId(null));
      datadogRum.addAction('explainability-evidence-close', { thresholdId });
    } else {
      dispatch(setSelectedEvidenceThresholdId(thresholdId));
      datadogRum.addAction('explainability-evidence-open', { thresholdId });
    }
  }, [currentThresholdId, dispatch, thresholdResult?.action_threshold_id]);

  const shouldRenderRuleExplainabilityContent = featureFlags?.ruleExplainabilityPhase > 0
    && !!thresholdResult && containerType !== ContainerType.TOOLTIP;

  return lineItems
    ? (
      <ThresholdContext.Provider value={thresholdContext}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <StyledRuleAndOutputWrapper
            justifyContent="flex-start"
            alignItems="stretch"
            containerType={containerType}
            featureFlags={featureFlags}
            isHighlighted={
              selectedEvidenceThresholdId === thresholdResult?.action_threshold_id
              || currentThresholdId === thresholdResult?.action_threshold_id
            }
            gap={3}
            ref={drop}
          >
            {renderRuleNumbers && (
            <RuleLine
              drag={isDragEnabled ? drag : undefined}
              isLineDraggable={isLineDraggable}
              key={semanticLineBreakData?.groupedByLines[lineIndex].id}
              lineObject={semanticLineBreakData?.groupedByLines[lineIndex]}
              lineIndex={lineIndex}
              setRuleItemHighlighted={setRuleItemHighlighted}
              totalNumRuleLines={semanticLineBreakData?.groupedByLines.length}
              parentRulePath={rulePath}
            />
            )}
            <StyledRuleLineItemWrapper
              direction="column"
              alignItems="flex-start"
              justifyContent={containerType === ContainerType.MODAL ? 'center' : 'flex-start'}
              containerType={containerType}
              gap={0}
              width="100%"
              lineWithoutClause={lineIndex > 0}
              editable={editable}
            >
              <StyledRuleItemRow>
                <StyledIndentation indentLevel={lineItemDepth}>
                  &nbsp;
                </StyledIndentation>
                <StyledHighlighter isHighlighted={isRuleItemHighlighted}>
                  <StyledRuleLineItem
                    ref={dragPreview}
                    containerType={containerType}
                    editable={editable}
                    simpleEditorMode={simpleEditorMode}
                    dimmed={isolatedEditingAreaEnabledForAnotherThreshold}
                  >
                    {(lineIndex === 0) && <RuleClause />}
                    {lineItems.map(item => renderRuleThresholdDropdown(item))}
                    {lineContainsThresholdCarets && (
                      <RuleLineThresholdItemWrapper
                        editable={editable}
                        lineItems={lineItems}
                        rulePath={rulePath}
                      />
                    )}
                    {PendingGroupEndParenthesisCaret}
                  </StyledRuleLineItem>
                </StyledHighlighter>
              </StyledRuleItemRow>
              {mlaEditorEnabledForThresholdAttribute && (
                <MLAAttributeContainer
                  indentLevel={lineItemDepth}
                  origin={mlaEditorEnabledForThresholdAttribute as MLAAttributeItemOrigin}
                  data={{ threshold: thresholdInThisLine }}
                />
              )}
              {isolatedTextEditingAreaEnabled && <IsolatedTextEditingContainer />}
            </StyledRuleLineItemWrapper>
            {shouldRenderRuleExplainabilityContent && (
              <StyledOutputValueWrapper simpleEditorMode={simpleEditorMode}>
                <Box justifyContent="space-between">
                  <RuleLineItemOutputValue
                    thresholdResult={thresholdResult}
                    showEvidenceButton={thresholdResult?.has_evidence}
                  />
                  {thresholdResult?.has_evidence && (
                    <StyledEvidencesButton
                      icon={IconName.Receipt}
                      size={ButtonSize.xsmall}
                      variant={ButtonVariant.tertiary}
                      onClick={evidenceButtonHandler}
                    />
                  )}
                </Box>
              </StyledOutputValueWrapper>
            )}
          </StyledRuleAndOutputWrapper>
          <StyledLineBreakDivider dragDropZone={isDragging} canDrop={canDrop} />
        </div>
      </ThresholdContext.Provider>
    )
    : null;
}

export default RuleLineItem;
